var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_OPTIONS, isEventSourceSupported, ReadyState, UNPARSABLE_JSON_OBJECT, } from './constants';
import { createOrJoinSocket } from './create-or-join';
import { getUrl } from './get-url';
import websocketWrapper from './proxy';
import { assertIsWebSocket } from './util';
export const useWebSocket = (url, options = DEFAULT_OPTIONS, connect = true) => {
    const [lastMessage, setLastMessage] = useState(null);
    const [readyState, setReadyState] = useState({});
    const lastJsonMessage = useMemo(() => {
        if (lastMessage) {
            try {
                return JSON.parse(lastMessage.data);
            }
            catch (e) {
                return UNPARSABLE_JSON_OBJECT;
            }
        }
        return null;
    }, [lastMessage]);
    const convertedUrl = useRef(null);
    const webSocketRef = useRef(null);
    const startRef = useRef(() => void 0);
    const reconnectCount = useRef(0);
    const messageQueue = useRef([]);
    const webSocketProxy = useRef(null);
    const optionsCache = useRef(options);
    optionsCache.current = options;
    const readyStateFromUrl = convertedUrl.current && readyState[convertedUrl.current] !== undefined
        ? readyState[convertedUrl.current]
        : url !== null && connect === true
            ? ReadyState.CONNECTING
            : ReadyState.UNINSTANTIATED;
    const stringifiedQueryParams = options.queryParams
        ? JSON.stringify(options.queryParams)
        : null;
    const sendMessage = useCallback((message, keep = true) => {
        var _a;
        if (isEventSourceSupported && webSocketRef.current instanceof EventSource) {
            // eslint-disable-next-line no-console
            console.warn('Unable to send a message from an eventSource');
            return;
        }
        if (((_a = webSocketRef.current) === null || _a === void 0 ? void 0 : _a.readyState) === ReadyState.OPEN) {
            assertIsWebSocket(webSocketRef.current);
            webSocketRef.current.send(message);
        }
        else if (keep) {
            messageQueue.current.push(message);
        }
    }, []);
    const sendJsonMessage = useCallback((message, keep = true) => {
        sendMessage(JSON.stringify(message), keep);
    }, [sendMessage]);
    const getWebSocket = useCallback(() => {
        if (optionsCache.current.share !== true ||
            (isEventSourceSupported && webSocketRef.current instanceof EventSource)) {
            return webSocketRef.current;
        }
        if (webSocketProxy.current === null && webSocketRef.current) {
            assertIsWebSocket(webSocketRef.current);
            webSocketProxy.current = websocketWrapper(webSocketRef.current, startRef);
        }
        return webSocketProxy.current;
    }, []);
    useEffect(() => {
        if (url !== null && connect === true) {
            let removeListeners;
            let expectClose = false;
            const start = () => __awaiter(void 0, void 0, void 0, function* () {
                convertedUrl.current = yield getUrl(url, optionsCache);
                const protectedSetLastMessage = (message) => {
                    if (!expectClose) {
                        setLastMessage(message);
                    }
                };
                const protectedSetReadyState = (state) => {
                    if (!expectClose) {
                        setReadyState((prev) => (Object.assign(Object.assign({}, prev), (convertedUrl.current && { [convertedUrl.current]: state }))));
                    }
                };
                removeListeners = createOrJoinSocket(webSocketRef, convertedUrl.current, protectedSetReadyState, optionsCache, protectedSetLastMessage, startRef, reconnectCount, sendMessage);
            });
            startRef.current = () => {
                if (!expectClose) {
                    if (webSocketProxy.current) {
                        webSocketProxy.current = null;
                    }
                    removeListeners === null || removeListeners === void 0 ? void 0 : removeListeners();
                    start();
                }
            };
            start();
            return () => {
                expectClose = true;
                if (webSocketProxy.current) {
                    webSocketProxy.current = null;
                }
                removeListeners === null || removeListeners === void 0 ? void 0 : removeListeners();
                setLastMessage(null);
            };
        }
        else if (url === null || connect === false) {
            reconnectCount.current = 0; // reset reconnection attempts
            setReadyState((prev) => (Object.assign(Object.assign({}, prev), (convertedUrl.current && {
                [convertedUrl.current]: ReadyState.CLOSED,
            }))));
        }
    }, [url, connect, stringifiedQueryParams, sendMessage]);
    useEffect(() => {
        if (readyStateFromUrl === ReadyState.OPEN) {
            messageQueue.current.splice(0).forEach((message) => {
                sendMessage(message);
            });
        }
    }, [readyStateFromUrl]);
    return {
        sendMessage,
        sendJsonMessage,
        lastMessage,
        lastJsonMessage,
        readyState: readyStateFromUrl,
        getWebSocket,
    };
};
